<template>
  <div class="segment-input-text">
    <BaseInputText
      v-model="value"
      :display-variables="originCall === 'conditional'"
      :variables-list="variablesList"
      :display-label="false"
      :debounce-delay="0"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, SetupContext, watch, onMounted, PropType, computed,
} from 'vue';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import allVariables from '@/components/template-builder/utils/variables-list';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';
import { getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { store } from '@/store';

export default defineComponent({
  name: 'SegmentInputText',

  components: {
    BaseInputText,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    availableContexts: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => ['contact'],
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: string; availableContexts: string[] }, { emit }: SetupContext) {
    const value = ref(props.modelValue);
    const variablesList = ref<any[]>([]);

    // Get originCall from store instead of prop
    const originCall = computed(() => store.getters['segmentationsEditor/getOriginCall']);

    watch(value, () => {
      emit('update:modelValue', value.value);
    });

    onMounted(async () => {
      // Filter variables based on available contexts
      const filteredVariables = allVariables.allVariables.filter((variable) => {
        const varValue = variable.value;

        // Check if the variable matches any of the available contexts
        return props.availableContexts.some((context) => varValue.includes(`${context}.`));
      });

      let baseVariables: any[] = [...filteredVariables];

      // Add custom contact properties
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      variablesList.value = baseVariables;
    });

    return {
      value,
      variablesList,
      originCall,
    };
  },
});
</script>

<style lang="scss" scoped>
.segment-input-text {
  width: 100%;

  // Override BaseInputText styles to match standard input appearance
  :deep(.input-variables-block) {
    .flex.flex-column {
      gap: 0 !important;
    }

    .p-fluid {
      display: flex;
      align-items: stretch;

      .input-text {
        flex: 1;
        border-top-left-radius: 3px !important;
        border-bottom-left-radius: 3px !important;
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        border-right: none !important;
        border-top: 1px solid #ced4da;
        border-left: 1px solid #ced4da;
        border-bottom: 1px solid #ced4da;
      }

      .input-dropdown-options-button {
        border-top: 1px solid #ced4da;
        border-right: 1px solid #ced4da;
        border-bottom: 1px solid #ced4da;
        border-left: none;
        background: white;
        color: var(--text-color-secondary);
        padding: 0 0.5rem;
        border-radius: 0 3px 3px 0;
        min-width: 2.5rem;

        &:hover {
          background: white;
          border-top: 1px solid #ced4da;
          border-right: 1px solid #ced4da;
          border-bottom: 1px solid #ced4da;
        }

        &:focus {
          box-shadow: none;
          border-top: 1px solid #ced4da;
          border-right: 1px solid #ced4da;
          border-bottom: 1px solid #ced4da;
        }
      }
    }

    // When variables selector is not displayed, restore full border-radius and border
    &:not(:has(.input-dropdown-options-button)) {
      .p-fluid .input-text {
        border-top-right-radius: 3px !important;
        border-bottom-right-radius: 3px !important;
        border-right: 1px solid #ced4da !important;
      }
    }
  }
}
</style>
